/**
 * 將 SynopsisWindow 的 plainText HTML 按行數分頁
 * @param {string} htmlString - 原始 HTML 字串
 * @param {number} lineHeight - 行高（像素）
 * @param {number} pageSize - 每頁最大行數
 * @param {HTMLElement} referenceContainer - 用於獲取樣式的參考容器
 * @returns {{htmlString: string, totalPages: number}} 分頁後的 HTML 字串和總頁數
 */
export const splitSynopsisIntoPages_PlainText = (htmlString, lineHeight, pageSize, referenceContainer) => {
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  
  // 提取外層包裝元素（如 <p class='MsoNormal'>）
  const outerWrappers = extractOuterWrappers(doc);
  
  // 找到 .plain_text 元素
  const plainTextElement = doc.body.querySelector('.plain_text');
  if (!plainTextElement) {
    // 如果找不到 .plain_text，直接返回原始 HTML
    return {
      htmlString: htmlString,
      totalPages: 1,
    };
  }
  
  // 提取原始元素信息
  const originalElementInfo = extractOriginalElementInfo(plainTextElement);
  
  // 創建測量容器
  const measureContainer = createMeasureContainer(referenceContainer, outerWrappers);
  
  // 直接找到 ExtendedContentContainer 元素（id="exContainer"）
  // 這樣 measureContainer 就能自然繼承 CSS module 的樣式（包括 br { display: none; }）
  const extendedContentContainer = document.getElementById('exContainer');
  if (extendedContentContainer) {
    extendedContentContainer.appendChild(measureContainer);
  } else {
    // 如果找不到 ExtendedContentContainer，則 fallback 到 document.body
    document.body.appendChild(measureContainer);
  }

  try {
    // 收集所有文字和元素節點（逐字符）
    const textContent = collectTextNodesFromBlock(plainTextElement);
    
    // 將內容切分成多頁
    const blockPages = splitBlockIntoPages(
      textContent,
      pageSize,
      measureContainer,
      lineHeight,
      plainTextElement,
      originalElementInfo
    );

    // 創建頁面元素
    const pages = [];
    blockPages.forEach((page) => {
      const pageDiv = doc.createElement('div');
      pageDiv.className = 'synopsis-page';
      
      const pageContent = page.html;
      
      // 用原始元素的結構包裹內容
      let wrappedContent = wrapWithOriginalElement(
        pageContent,
        originalElementInfo,
        doc
      );
      
      pageDiv.appendChild(wrappedContent);
      pages.push(pageDiv);
    });
    
    // 創建一個臨時容器來收集所有頁面元素
    const container = doc.createElement('div');
    pages.forEach(page => {
      container.appendChild(page);
    });
    
    // 用外層包裝元素包裹所有頁面
    const finalHTML = wrapWithOuterWrappers(container.innerHTML, outerWrappers, doc);
    
    return {
      htmlString: finalHTML,
      totalPages: pages.length || 1,
    };
  } finally {
    // 從父容器中移除 measureContainer
    if (measureContainer.parentNode) {
      measureContainer.parentNode.removeChild(measureContainer);
    }
  }
};

/**
 * 提取外層包裝元素（從 body 的直接子節點開始，向上查找所有包裝元素）
 * 例如：<p class='MsoNormal'><div class='plain_text'>...</div></p>
 * @param {Document} doc - DOMParser 解析後的文檔
 * @returns {Array<{tagName: string, className: string, id: string, attributes: string}>}
 */
const extractOuterWrappers = (doc) => {
  const wrappers = [];
  const body = doc.body;
  
  // 找到 .plain_text 元素
  const plainTextEl = body.querySelector('.plain_text');
  
  if (plainTextEl) {
    // 向上查找所有父元素，直到 body
    let current = plainTextEl.parentElement;
    while (current && current !== body && current !== doc.documentElement) {
      // 提取元素信息
      const wrapperInfo = {
        tagName: current.tagName.toLowerCase(),
        className: current.className || '',
        id: current.id || '',
        attributes: getAttributesStringExcluding(current, ['class', 'id'])
      };
      
      // 只添加有 className 或 id 的元素，或者是常見的包裝元素（p, div）
      if (wrapperInfo.className || wrapperInfo.id || 
          ['p', 'div', 'section', 'article'].includes(wrapperInfo.tagName)) {
        wrappers.unshift(wrapperInfo); // 從外層到內層的順序
      }
      
      current = current.parentElement;
    }
  }
  
  return wrappers;
};

/**
 * 用外層包裝元素包裹 HTML 內容
 * @param {string} htmlContent - 要包裹的 HTML 內容
 * @param {Array} outerWrappers - 外層包裝元素信息數組
 * @param {Document} doc - 文檔對象
 * @returns {string} 包裹後的 HTML 字串
 */
const wrapWithOuterWrappers = (htmlContent, outerWrappers, doc) => {
  if (!outerWrappers || outerWrappers.length === 0) {
    return htmlContent;
  }
  
  // 從最內層到最外層依次包裹
  let wrappedContent = htmlContent;
  
  for (const wrapper of outerWrappers) {
    let tag = `<${wrapper.tagName}`;
    if (wrapper.id) {
      tag += ` id="${wrapper.id}"`;
    }
    if (wrapper.className) {
      tag += ` class="${wrapper.className}"`;
    }
    if (wrapper.attributes) {
      tag += wrapper.attributes;
    }
    tag += '>';
    
    wrappedContent = tag + wrappedContent + `</${wrapper.tagName}>`;
  }
  
  return wrappedContent;
};

/**
 * 創建用於測量的臨時容器
 * @param {HTMLElement} referenceContainer - 參考容器
 * @param {Array} outerWrappers - 外層包裝元素信息（包含 .plain_text 等）
 */
const createMeasureContainer = (referenceContainer, outerWrappers) => {
  const measureContainer = document.createElement('div');
  const containerStyle = window.getComputedStyle(referenceContainer);
  
  measureContainer.style.position = 'absolute';
  measureContainer.style.visibility = 'hidden';
  measureContainer.style.width = referenceContainer.offsetWidth + 'px';
  measureContainer.style.fontSize = containerStyle.fontSize;
  measureContainer.style.lineHeight = containerStyle.lineHeight;
  measureContainer.style.fontFamily = containerStyle.fontFamily;
  
  // 創建外層包裝元素的 DOM 結構（如 .plain_text），確保 CSS 選擇器能正確匹配
  // 注意：br { display: none; } 等樣式會從父容器（ExtendedContentContainer）自然繼承
  if (outerWrappers && outerWrappers.length > 0) {
    let currentContainer = measureContainer;
    
    // 從外層到內層依次創建包裝元素
    outerWrappers.forEach((wrapper) => {
      const wrapperEl = document.createElement(wrapper.tagName);
      if (wrapper.className) {
        wrapperEl.className = wrapper.className;
      }
      if (wrapper.id) {
        wrapperEl.id = wrapper.id;
      }
      
      // 設置其他屬性
      if (wrapper.attributes) {
        const parser = new DOMParser();
        const tempDoc = parser.parseFromString(`<div ${wrapper.attributes}></div>`, 'text/html');
        const tempEl = tempDoc.body.firstElementChild;
        if (tempEl) {
          Array.from(tempEl.attributes).forEach(attr => {
            if (attr.name !== 'class' && attr.name !== 'id') {
              wrapperEl.setAttribute(attr.name, attr.value);
            }
          });
        }
      }
      
      currentContainer.appendChild(wrapperEl);
      currentContainer = wrapperEl;
    });
    
    // 創建一個內部容器用於放置測量內容
    const innerContainer = document.createElement('div');
    currentContainer.appendChild(innerContainer);
    measureContainer._innerContainer = innerContainer;
  }
  
  return measureContainer;
};

/**
 * 從區塊元素中收集所有文字和元素節點
 * 保留所有 HTML 結構（包括 <br>、div 等），以便正確測量高度和分頁
 * .img div 會被標記為特殊類型，在分頁時會獨立成頁
 */
const collectTextNodesFromBlock = (blockElement) => {
  const nodes = [];
  const childNodes = Array.from(blockElement.childNodes);
  const processedIndices = new Set();
  
  // 先找到 .title 元素的位置（如果存在）
  let titleIndex = -1;
  for (let j = 0; j < childNodes.length; j++) {
    const childNode = childNodes[j];
    if (childNode.nodeType === Node.ELEMENT_NODE) {
      const hasTitleClass = childNode.classList && childNode.classList.contains('title');
      if (hasTitleClass) {
        titleIndex = j;
        break;
      }
    }
  }
  
  // 處理文字節點的共用函數
  const processTextNode = (text, result) => {
    if (text.trim()) {
      // 將文字按照英文單字和中文/其他字符分組
      // 英文單字：連續的字母、數字、連字符、撇號等
      // 中文和其他字符：逐字符處理
      const wordPattern = /[\w'-]+/g; // 匹配英文單字（包括字母、數字、連字符、撇號）
      let lastIndex = 0;
      let match;
      
      while ((match = wordPattern.exec(text)) !== null) {
        // 添加英文單字前的非英文字符（逐字符）
        if (match.index > lastIndex) {
          const nonWordText = text.substring(lastIndex, match.index);
          for (const char of nonWordText) {
            result.push({ 
              type: 'text', 
              content: char
            });
          }
        }
        
        // 添加英文單字作為整體
        result.push({ 
          type: 'text', 
          content: match[0]
        });
        
        lastIndex = match.index + match[0].length;
      }
      
      // 添加剩餘的非英文字符（逐字符）
      if (lastIndex < text.length) {
        const remainingText = text.substring(lastIndex);
        for (const char of remainingText) {
          result.push({ 
            type: 'text', 
            content: char
          });
        }
      }
    } else if (text) {
      // 保留空白文字（包括換行符等）
      result.push({ 
        type: 'text', 
        content: text
      });
    }
  };
  
  // 先處理所有節點，識別 .img div
  for (let i = 0; i < childNodes.length; i++) {
    if (processedIndices.has(i)) continue;
    
    const node = childNodes[i];
    
    // 處理非元素節點（文字節點等）
    if (node.nodeType !== Node.ELEMENT_NODE) {
      const text = node.textContent;
      processTextNode(text, nodes);
      continue;
    }
    
    const element = node;
    
    // 檢查元素是否包含 'img' class
    const isImgContainer = element.classList && element.classList.contains('img');
    
    // 如果遇到圖片元素
    if (isImgContainer) {
      // 只有在「有 .title 且圖片在 .title 之前」時，才不獨立成頁
      // 沒有 .title 時，圖片應獨立成頁
      const isBeforeTitle = titleIndex !== -1 && i < titleIndex;

      
      // 如果圖片在 .title 之前，不獨立成頁，當作普通元素處理
      if (isBeforeTitle) {
        // 檢查前面是否有 <br>
        let imgHTML = '';
        if (i > 0) {
          const prevNode = childNodes[i - 1];
          if (prevNode.nodeType === Node.ELEMENT_NODE && prevNode.tagName === 'BR') {
            // 前面有 <br>，一起收集
            imgHTML = prevNode.outerHTML;
            processedIndices.add(i - 1);
          }
        }
        
        imgHTML += element.outerHTML;
        
        // 檢查 .img div 後面是否有 <br>
        if (i + 1 < childNodes.length) {
          const nextNode = childNodes[i + 1];
          if (nextNode.nodeType === Node.ELEMENT_NODE && nextNode.tagName === 'BR') {
            // 有 <br>，一起收集
            imgHTML += nextNode.outerHTML;
            processedIndices.add(i + 1);
          }
        }
        
        // 當作普通元素處理（遞迴處理）
        const collect = (node, result) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent;
            processTextNode(text, result);
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const isEquation = node.classList && node.classList.contains('MTConvertedEquation');
            // 對於自閉合標籤（如 <br>），直接保留 outerHTML
            if (isSelfClosingTag(node.tagName)) {
              result.push({ 
                type: 'equation', 
                content: node.outerHTML
              });
            }
            // 對於不可分割的單元（算式），保留完整的 HTML
            else if (isEquation) {
              result.push({ 
                type: 'equation', 
                content: node.outerHTML
              });
            } 
            // 對於容器元素（div 等），保留標籤結構但內容需要拆分成字符
            else {
              // 添加開始標籤（包含所有屬性）
              const startTag = `<${node.tagName.toLowerCase()}${getAttributesString(node)}>`;
              result.push({ 
                type: 'equation', 
                content: startTag
              });
              
              // 遞迴處理子節點（文字會被拆分成字符）
              Array.from(node.childNodes).forEach(child => {
                collect(child, result);
              });
              
              // 添加結束標籤
              result.push({ 
                type: 'equation', 
                content: `</${node.tagName.toLowerCase()}>`
              });
            }
          }
        };
        
        // 解析 imgHTML 並遞迴處理
        const parser = new DOMParser();
        const tempDoc = parser.parseFromString(imgHTML, 'text/html');
        Array.from(tempDoc.body.childNodes).forEach(child => {
          collect(child, nodes);
        });
        
        processedIndices.add(i);
        continue;
      } else {
        // 圖片在 .title 之後，獨立成頁
        let imgHTML = element.outerHTML;
        
        // 檢查 .img div 後面是否有 <br>
        if (i + 1 < childNodes.length) {
          const nextNode = childNodes[i + 1];
          if (nextNode.nodeType === Node.ELEMENT_NODE && nextNode.tagName === 'BR') {
            // 有 <br>，一起收集
            imgHTML += nextNode.outerHTML;
            processedIndices.add(i + 1);
          }
        }
        // 將 .img div 標記為特殊類型
        nodes.push({
          type: 'img',
          content: imgHTML
        });
        processedIndices.add(i);
        continue;
      }
    }
    
    // 其他元素，遞迴處理
    const collect = (node, result) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        processTextNode(text, result);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const isEquation = node.classList && node.classList.contains('MTConvertedEquation');
        // 對於自閉合標籤（如 <br>），直接保留 outerHTML
        if (isSelfClosingTag(node.tagName)) {
          result.push({ 
            type: 'equation', 
            content: node.outerHTML
          });
        }
        // 對於不可分割的單元（算式），保留完整的 HTML
        else if (isEquation) {
          result.push({ 
            type: 'equation', 
            content: node.outerHTML
          });
        } 
        // 對於容器元素（div 等），保留標籤結構但內容需要拆分成字符
        else {
          // 添加開始標籤（包含所有屬性）
          const startTag = `<${node.tagName.toLowerCase()}${getAttributesString(node)}>`;
          result.push({ 
            type: 'equation', 
            content: startTag
          });
          
          // 遞迴處理子節點（文字會被拆分成字符）
          Array.from(node.childNodes).forEach(child => {
            collect(child, result);
          });
          
          // 添加結束標籤
          result.push({ 
            type: 'equation', 
            content: `</${node.tagName.toLowerCase()}>`
          });
        }
      }
    };
    
    collect(element, nodes);
  }
  
  return nodes;
};

/**
 * 獲取元素的屬性字串
 */
const getAttributesString = (element) => {
  let attrs = '';
  if (element.attributes && element.attributes.length > 0) {
    Array.from(element.attributes).forEach(attr => {
      attrs += ` ${attr.name}="${attr.value}"`;
    });
  }
  return attrs;
};

/**
 * 從元素中提取原始元素信息（className, id, tagName）
 * 會向上查找，直到找到有 className 或 id 的元素
 */
const extractOriginalElementInfo = (element) => {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  
  // 如果當前元素有 className 或 id，直接返回
  if (element.className || element.id) {
    return {
      tagName: element.tagName.toLowerCase(),
      className: element.className || '',
      id: element.id || '',
      attributes: getAttributesStringExcluding(element, ['class', 'id'])
    };
  }
  
  // 否則向上查找父元素
  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    if (parent.className || parent.id) {
      return {
        tagName: parent.tagName.toLowerCase(),
        className: parent.className || '',
        id: parent.id || '',
        attributes: getAttributesStringExcluding(parent, ['class', 'id'])
      };
    }
    parent = parent.parentElement;
  }
  
  return null;
};

/**
 * 獲取元素的屬性字串（排除指定的屬性）
 */
const getAttributesStringExcluding = (element, excludeAttrs = []) => {
  let attrs = '';
  if (element.attributes && element.attributes.length > 0) {
    Array.from(element.attributes).forEach(attr => {
      if (!excludeAttrs.includes(attr.name)) {
        attrs += ` ${attr.name}="${attr.value}"`;
      }
    });
  }
  return attrs;
};

/**
 * 判斷是否為自閉合標籤
 */
const isSelfClosingTag = (tagName) => {
  const selfClosingTags = ['BR', 'IMG', 'HR', 'INPUT', 'META', 'LINK', 'AREA', 'BASE', 'COL', 'EMBED', 'SOURCE', 'TRACK', 'WBR'];
  return selfClosingTags.includes(tagName.toUpperCase());
};

/**
 * 用原始元素的結構包裹內容
 */
const wrapWithOriginalElement = (content, originalElementInfo, doc) => {
  if (!originalElementInfo || (!originalElementInfo.className && !originalElementInfo.id)) {
    // 沒有原始元素信息，直接創建一個 div 包裹內容
    if (typeof content === 'string') {
      const wrapper = doc.createElement('div');
      wrapper.innerHTML = content;
      return wrapper;
    }
    return content;
  }
  
  // 創建原始元素的結構
  const wrapper = doc.createElement(originalElementInfo.tagName);
  if (originalElementInfo.className) {
    wrapper.className = originalElementInfo.className;
  }
  if (originalElementInfo.id) {
    wrapper.id = originalElementInfo.id;
  }
  
  // 設置其他屬性
  if (originalElementInfo.attributes) {
    const parser = new DOMParser();
    const tempDoc = parser.parseFromString(`<div ${originalElementInfo.attributes}></div>`, 'text/html');
    const tempEl = tempDoc.body.firstElementChild;
    if (tempEl) {
      Array.from(tempEl.attributes).forEach(attr => {
        if (attr.name !== 'class' && attr.name !== 'id') {
          wrapper.setAttribute(attr.name, attr.value);
        }
      });
    }
  }
  
  // 設置內容
  if (typeof content === 'string') {
    wrapper.innerHTML = content;
  } else {
    wrapper.appendChild(content);
  }
  
  return wrapper;
};

/**
 * 構建包裹標籤（用於測量）
 */
const buildWrapperTag = (originalElementInfo) => {
  if (!originalElementInfo || (!originalElementInfo.className && !originalElementInfo.id)) {
    return { startTag: '<div>', endTag: '</div>' };
  }
  
  let tag = `<${originalElementInfo.tagName}`;
  if (originalElementInfo.id) {
    tag += ` id="${originalElementInfo.id}"`;
  }
  if (originalElementInfo.className) {
    tag += ` class="${originalElementInfo.className}"`;
  }
  if (originalElementInfo.attributes) {
    tag += originalElementInfo.attributes;
  }
  tag += '>';
  
  return {
    startTag: tag,
    endTag: `</${originalElementInfo.tagName}>`
  };
};

/**
 * 將節點陣列切分成多頁
 * 返回包含 HTML 的對象陣列
 */
const splitBlockIntoPages = (nodes, pageSize, measureContainer, lineHeight, originalBlock, originalElementInfo) => {
  const pages = [];
  let currentPageHTML = '';
  
  // 構建包裹標籤（用於測量）
  const { startTag, endTag } = buildWrapperTag(originalElementInfo);
  
  // 如果測量容器有 _innerContainer（包含外層包裝結構），將 HTML 放入內層容器
  const contentContainer = measureContainer._innerContainer || measureContainer;
  
  // 每一頁的最大高度（7 行）
  const maxHeight = pageSize * lineHeight;
  
  nodes.forEach((node) => {
    const nodeContent = node.content;
    const nodeType = node.type;
    
    if (!nodeContent) return;

    // 如果遇到圖片節點，圖片自己佔一頁
    if (nodeType === 'img') {
      // 如果當前頁有內容，先保存當前頁
      if (currentPageHTML) {
        pages.push({
          html: currentPageHTML
        });
        currentPageHTML = '';
      }
      
      // 圖片自己佔一頁
      pages.push({
        html: nodeContent
      });
      return;
    }

    // 文字節點：測量加入當前節點後的總高度（使用原始元素的結構包裹）
    let testHTML = startTag + currentPageHTML + nodeContent + endTag;
    contentContainer.innerHTML = testHTML;
    // 使用最外層容器的 offsetHeight（包含所有 CSS 樣式的影響）
    const testHeight = measureContainer.offsetHeight;

    if (testHeight > maxHeight + 1 && currentPageHTML) {
      // 超過限制，切到新頁
      pages.push({
        html: currentPageHTML
      });
      currentPageHTML = nodeContent;
    } else {
      currentPageHTML += nodeContent;
    }
  });
  
  // 最後一頁（文字內容）
  if (currentPageHTML) {
    pages.push({
      html: currentPageHTML
    });
  }
  
  // 如果最後一頁只有一行，合併到上一頁
  // 但如果最後一頁是圖片，則不進行合併（圖片自己佔一頁）
  if (pages.length > 1) {
    const lastPage = pages[pages.length - 1];
    const lastPageHTML = lastPage.html;
    
    // 檢查最後一頁是否包含圖片標記（.img div）
    const isLastPageImage = lastPageHTML.includes('class="img') || lastPageHTML.includes("class='img");
    
    // 如果最後一頁不是圖片，才進行合併檢查
    if (!isLastPageImage) {
      // 測量最後一頁的高度
      const testHTML = startTag + lastPageHTML + endTag;
      contentContainer.innerHTML = testHTML;
      const lastPageHeight = measureContainer.offsetHeight;
      
      // 如果最後一頁的高度小於等於一行，合併到上一頁
      if (lastPageHeight <= lineHeight + 1) {
        const previousPage = pages[pages.length - 2];
        previousPage.html += lastPageHTML;
        pages.pop();
      }
    }
  }
  
  return pages.length > 0 ? pages : [{ html: '' }];
};

